import { Grid, Typography } from "@mui/material";
import { useState } from "react";
import BloodGlucoseInput from "../../../../pages/health/encounters/cards/vitals/components/BloodGlucoseInput";
import BloodPressureInput from "../../../../pages/health/encounters/cards/vitals/components/BloodPressureInput";
import BmiInput from "../../../../pages/health/encounters/cards/vitals/components/BmiInput";
import BsaInput from "../../../../pages/health/encounters/cards/vitals/components/BsaInput";
import HeadCircumferenceInput from "../../../../pages/health/encounters/cards/vitals/components/HeadCircumferenceInput";
import HeartRateInput from "../../../../pages/health/encounters/cards/vitals/components/HeartRateInput";
import HeightInput from "../../../../pages/health/encounters/cards/vitals/components/HeightInput";
import OxygenSaturationInput from "../../../../pages/health/encounters/cards/vitals/components/OxygenSaturationInput";
import PainInput from "../../../../pages/health/encounters/cards/vitals/components/PainInput";
import RespiratoryRateInput from "../../../../pages/health/encounters/cards/vitals/components/RespiratoryRateInput";
import TemperatureInput from "../../../../pages/health/encounters/cards/vitals/components/TemperatureInput";
import WaistInput from "../../../../pages/health/encounters/cards/vitals/components/WaistInput";
import WeightInput from "../../../../pages/health/encounters/cards/vitals/components/WeightInput";

type VitalsInputFormProps = {
  value: Record<string, any>;
  patient: any;
  onChange: (value: any, modifier: boolean) => void;
  disabled: boolean;
  size?: "small" | "medium";
  omitPainScale?: boolean;
  title?: string;
  type?: "encounter-modal" | "simplified-vitals" | "detailed-vitals";
};

const VitalsInputForm = ({
  value,
  patient,
  onChange,
  disabled,
  size,
  omitPainScale = false,
  title,
  type = "detailed-vitals",
}: VitalsInputFormProps) => {
  const [painPresent, setPainPresent] = useState(false);

  const isEncounterModal = type === "encounter-modal";
  const isSimplifiedVitals = type === "simplified-vitals";
  const isDetailedVitals = type === "detailed-vitals";

  const heartRateInput = (
    <HeartRateInput
      disabled={disabled}
      value={value?.heartRate ?? ""}
      onChange={(newHeartRate) => {
        onChange({ ...value, heartRate: newHeartRate }, true);
      }}
      size="small"
      type={type}
    />
  );

  return (
    <Grid item xs={12}>
      <Grid container spacing={1}>
        {!title && (
          <Grid item xs={12}>
            <Typography variant="subtitle2">Vitals</Typography>
          </Grid>
        )}

        <Grid item xs={12}>
          <Grid container columns={isEncounterModal ? 8 : 10} spacing={2}>
            <Grid item xs={2}>
              <HeightInput
                disabled={disabled}
                value={value?.height ?? ""}
                patient={patient}
                onChange={(newHeight) => {
                  onChange({ ...value, height: newHeight }, true);
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={2}>
              <WeightInput
                disabled={disabled}
                patient={patient}
                value={value?.weight ?? ""}
                onChange={(newWeight) => {
                  onChange({ ...value, weight: newWeight }, true);
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={2}>
              <BmiInput
                disabled={disabled}
                height={value?.height}
                weight={value?.weight}
                value={value?.bmi ?? ""}
                onChange={(newBmi) => {
                  onChange({ ...value, bmi: newBmi }, false);
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={2}>
              <BsaInput
                disabled={disabled}
                height={value?.height}
                weight={value?.weight}
                value={value?.bsa ?? ""}
                onChange={(newBsa) => {
                  onChange({ ...value, bsa: newBsa }, false);
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={2}>
              <WaistInput
                disabled={disabled}
                value={value?.waist ?? ""}
                patient={patient}
                onChange={(newWaist) => {
                  onChange({ ...value, waist: newWaist }, true);
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={2}>
              <BloodPressureInput
                disabled={disabled}
                value={value?.bloodPressure ?? ""}
                onChange={(newBloodPressure) => {
                  onChange({ ...value, bloodPressure: newBloodPressure }, true);
                }}
                size={size ?? "small"}
                type="modal"
              />
            </Grid>
            <Grid item xs={2}>
              <Grid container>
                <OxygenSaturationInput
                  disabled={disabled}
                  value={value?.oxygenSaturation ?? ""}
                  onChange={(newOxygenSat) => {
                    onChange({ ...value, oxygenSat: newOxygenSat }, true);
                  }}
                  size="small"
                />
              </Grid>
            </Grid>

            <Grid item xs={4}>
              <TemperatureInput
                disabled={disabled}
                value={value?.temperature ?? ""}
                onChange={(newTemperature) => {
                  onChange({ ...value, temperature: newTemperature }, true);
                }}
                size="small"
              />
            </Grid>

            <Grid item xs={2}>
              <HeadCircumferenceInput
                disabled={disabled}
                value={value?.headCircumference ?? ""}
                size="small"
                onChange={(hc) =>
                  onChange({ ...value, headCircumference: hc }, true)
                }
              />
            </Grid>

            {isSimplifiedVitals && (
              <Grid item xs={4}>
                <BloodGlucoseInput
                  disabled={disabled}
                  value={value?.bloodGlucose ?? ""}
                  onChange={(bgl) => {
                    onChange({ ...value, bloodGlucose: bgl }, true);
                  }}
                  size="small"
                />
              </Grid>
            )}

            {isSimplifiedVitals ? (
              heartRateInput
            ) : (
              <Grid item xs={isEncounterModal ? 6 : 4} display={"flex"} gap={1}>
                {heartRateInput}
              </Grid>
            )}
            <Grid item xs={2}>
              <RespiratoryRateInput
                disabled={disabled}
                value={value?.respiratoryRate ?? ""}
                onChange={(newRespiratoryRate) => {
                  onChange(
                    { ...value, respiratoryRate: newRespiratoryRate },
                    true,
                  );
                }}
                size="small"
                isEncounterModal={isEncounterModal}
              />
            </Grid>

            {isEncounterModal && (
              <Grid item xs={4}>
                <BloodGlucoseInput
                  disabled={disabled}
                  value={value?.bloodGlucose ?? ""}
                  onChange={(bgl) => {
                    onChange({ ...value, bloodGlucose: bgl }, true);
                  }}
                  size="small"
                />
              </Grid>
            )}

            {!omitPainScale && (
              <Grid item xs={12} mt={1}>
                <PainInput
                  patient={patient}
                  disabled={disabled || !painPresent}
                  value={value?.pain}
                  onChange={(newPain) => {
                    onChange({ ...value, pain: newPain }, true);
                  }}
                  size={size}
                  showCheckbox={true}
                  capturePain={painPresent}
                  onChangeExperiencingPain={setPainPresent}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default VitalsInputForm;
